import { readEnvKey } from './chunk-OZHLQIE6.js';
import { DEFAULT_RBAC_POLICY, policyDataEnv, parsePolicyCsv, permissionSetFromPolicy, MANAGE_ACTION } from './chunk-M4KFZRI3.js';
import { storageKey, DEFAULT_CLAIMS_NAMESPACE } from './chunk-XHKWG5FD.js';
import { singleflight } from './chunk-7B3IWQPL.js';
import { resolveAppOrigin, resolveAppUrl, APP_REGISTRY, resolveAppBasePath, LOGIN_APP, getAppEntry } from './chunk-O2TUCS6S.js';
import { brandClass, globalSingleton } from './chunk-N7SZ66QO.js';

// src/auth/prefetch.ts
var APP_PREFETCH = {
  // dashboard is the default external destination — warm its entry document.
  dashboard: { documents: ["/"] }
};
function platformPreconnect(ctx) {
  const hosts = /* @__PURE__ */ new Set();
  hosts.add(`https://api.${ctx.rootDomain}`);
  if (ctx.auth0Domain) hosts.add(`https://${ctx.auth0Domain}`);
  for (const h of ctx.extraPreconnect ?? []) hosts.add(h);
  return [...hosts];
}
function resolveAppPrefetch(application, ctx) {
  const origin = resolveAppOrigin(application, ctx);
  const hints = APP_PREFETCH[application] ?? {};
  const docs = hints.documents ?? ["/"];
  return {
    preconnect: [origin, ...hints.preconnect ?? []],
    documents: docs.map((path) => resolveAppUrl({ application, path }, ctx)),
    modules: (hints.modules ?? []).map((m) => `${origin}${m.startsWith("/") ? m : `/${m}`}`)
  };
}
function buildPrefetchPlan(opts) {
  const preconnect = /* @__PURE__ */ new Set();
  const documents = /* @__PURE__ */ new Set();
  const modules = /* @__PURE__ */ new Set();
  if (opts.includePlatform !== false) {
    for (const host of platformPreconnect(opts.ctx)) preconnect.add(host);
  }
  if (opts.returnTo) {
    try {
      const url = new URL(opts.returnTo);
      const app = Object.keys(APP_REGISTRY).filter((key) => !APP_REGISTRY[key].preAuth).sort((a, b) => resolveAppBasePath(b).length - resolveAppBasePath(a).length).find((key) => resolveAppOrigin(key, opts.ctx) === url.origin && url.pathname.startsWith(resolveAppBasePath(key)));
      if (app && !url.username && !url.password) {
        const plan = resolveAppPrefetch(app, opts.ctx);
        plan.preconnect.forEach((origin) => preconnect.add(origin));
        plan.documents.forEach((href) => documents.add(href));
      }
    } catch {
    }
  }
  const apps = opts.apps ?? Object.keys(APP_REGISTRY).filter((app) => {
    const entry = APP_REGISTRY[app];
    return app !== LOGIN_APP && !entry.preAuth && !entry.aliasOf;
  });
  for (const app of apps) {
    if (!APP_REGISTRY[app]) continue;
    if (getAppEntry(app).type === "internal" && !opts.includeInternal) continue;
    const plan = resolveAppPrefetch(app, opts.ctx);
    for (const p of plan.preconnect) preconnect.add(p);
    for (const d of plan.documents) documents.add(d);
    for (const m of plan.modules) modules.add(m);
  }
  return { preconnect: [...preconnect], documents: [...documents], modules: [...modules] };
}
function entryAssets(html, documentUrl, Parser) {
  const base = new URL(documentUrl);
  const root = base.pathname.replace(/[^/]*$/, "");
  const parsed = new Parser().parseFromString(html, "text/html");
  const assets = /* @__PURE__ */ new Set();
  for (const node of parsed.querySelectorAll(
    'script[type="module"][src], link[rel="modulepreload"][href], link[rel="stylesheet"][href]'
  )) {
    try {
      const url = new URL(node.getAttribute("src") ?? node.getAttribute("href"), base);
      if (url.origin === base.origin && url.pathname.startsWith(`${root}assets/`) && /\.(?:js|css)$/.test(url.pathname) && !url.search && !url.hash && !url.username && !url.password) assets.add(url.href);
    } catch {
    }
  }
  return [...assets].slice(0, 24);
}
function applyPrefetchPlan(plan, doc) {
  const target = doc ?? (typeof document !== "undefined" ? document : void 0);
  const win = target?.defaultView;
  if (!target?.head || !win) return () => {
  };
  const connection = win.navigator.connection;
  if (connection?.saveData || /^(slow-)?2g$/.test(connection?.effectiveType ?? "")) return () => {
  };
  const created = [];
  const add = (rel, href) => {
    if ([...target.head.querySelectorAll("link")].some((el) => el.rel === rel && el.href === new URL(href, target.baseURI).href)) return;
    const link = target.createElement("link");
    link.rel = rel;
    link.href = href;
    if (rel === "preconnect") link.crossOrigin = "anonymous";
    target.head.appendChild(link);
    created.push(link);
    return link;
  };
  for (const origin of plan.preconnect) {
    add("preconnect", origin);
    add("dns-prefetch", origin);
  }
  const controller = new AbortController();
  const fetched = /* @__PURE__ */ new Set();
  const fetchStatic = async (href, html = false) => {
    const response = await win.fetch(href, {
      signal: AbortSignal.any([controller.signal, AbortSignal.timeout(1e4)]),
      credentials: "same-origin",
      mode: "cors",
      redirect: "error",
      priority: "low",
      cache: html ? "no-cache" : "default"
    });
    if (!response.ok) throw new Error("Static warmup unavailable");
    return response;
  };
  const warmAsset = async (href) => {
    if (fetched.has(href) || controller.signal.aborted) return;
    fetched.add(href);
    const marker = add("authe-prefetch", href);
    if (marker) marker.dataset.prefetchState = "loading";
    try {
      const response = await fetchStatic(href);
      if (!/(?:javascript|css)/i.test(response.headers.get("content-type") ?? "")) throw new Error("Not an asset");
      await response.arrayBuffer();
      if (marker) marker.dataset.prefetchState = "complete";
    } catch {
      if (marker) marker.dataset.prefetchState = "unavailable";
    }
  };
  const run = async () => {
    for (const href of new Set(plan.documents)) {
      if (controller.signal.aborted) return;
      try {
        const response = await fetchStatic(href, true);
        if (!response.headers.get("content-type")?.includes("text/html")) continue;
        const urls = entryAssets(await response.text(), href, win.DOMParser);
        for (let i = 0; i < urls.length; i += 2) {
          if (controller.signal.aborted) return;
          await Promise.all(urls.slice(i, i + 2).map(warmAsset));
        }
      } catch {
      }
    }
    for (const href of plan.modules) await warmAsset(href);
  };
  let idle;
  let timer;
  const schedule = () => {
    if (target.visibilityState === "hidden" || controller.signal.aborted) return;
    target.removeEventListener("visibilitychange", schedule);
    if (typeof win.requestIdleCallback === "function") idle = win.requestIdleCallback(() => {
      void run();
    }, { timeout: 2e3 });
    else timer = win.setTimeout(() => {
      void run();
    }, 200);
  };
  const loaded = () => {
    if (target.visibilityState === "hidden") target.addEventListener("visibilitychange", schedule);
    else schedule();
  };
  if (target.readyState === "complete") loaded();
  else win.addEventListener("load", loaded, { once: true });
  return () => {
    controller.abort();
    win.removeEventListener("load", loaded);
    target.removeEventListener("visibilitychange", schedule);
    if (idle !== void 0) win.cancelIdleCallback(idle);
    if (timer !== void 0) win.clearTimeout(timer);
    created.forEach((link) => link.remove());
  };
}

// src/auth/dev-auth.ts
function createMockToken(input = {}, namespace = DEFAULT_CLAIMS_NAMESPACE) {
  const ns = namespace.endsWith("/") ? namespace : `${namespace}/`;
  const { sub, email, ttlSeconds, ...claims } = input;
  const now = Math.floor(Date.now() / 1e3);
  const payload = {
    sub: sub ?? "auth0|local-dev-user",
    email: email ?? "dev@localhost",
    iss: ns,
    iat: now,
    exp: now + (ttlSeconds ?? 3600)
  };
  for (const [key, value] of Object.entries(claims)) {
    if (value !== void 0) payload[ns + key] = value;
  }
  return payload;
}
function isDevAuthEnabled(opts = {}) {
  if (opts.env === "prd") return false;
  const search = opts.search ?? (typeof window !== "undefined" ? window.location.search : "");
  const value = new URLSearchParams(search).get("devauth");
  return value != null && value !== "0" && value !== "false";
}

// src/auth/impersonation.ts
var INACTIVE = {
  active: false,
  actorSub: null,
  targetSub: null,
  sessionId: null,
  reason: null,
  expiresAt: null,
  expired: false,
  accessLevel: "read_only"
};
function expiryPassed(expiresAt, now) {
  if (!expiresAt) return false;
  const ts = Date.parse(expiresAt);
  return Number.isFinite(ts) && ts <= now;
}
function getImpersonationState(claims, options = {}) {
  const actorSub = claims?.act?.sub ?? null;
  if (!claims || claims.impersonated !== true || !actorSub) return INACTIVE;
  const now = options.now ?? Date.now();
  const expiresAt = claims.impersonation_expires_at ?? null;
  return {
    active: true,
    actorSub,
    targetSub: options.targetSub ?? null,
    sessionId: claims.impersonation_id ?? null,
    reason: claims.impersonation_reason ?? null,
    expiresAt,
    expired: expiryPassed(expiresAt, now),
    // Anything unrecognised reads as read_only, matching the backend: a
    // malformed claim must never be the thing that implies write access.
    accessLevel: claims.impersonation_access_level === "read_write" ? "read_write" : "read_only"
  };
}
function isImpersonating(claims) {
  return claims?.impersonated === true && !!claims?.act?.sub;
}
function isImpersonationExpired(claims, now = Date.now()) {
  if (!isImpersonating(claims)) return false;
  return expiryPassed(claims?.impersonation_expires_at, now);
}
function impersonationBanner(claims, options = {}) {
  const state = getImpersonationState(claims, options);
  if (!state.active) return null;
  const who = state.targetSub ? `user ${state.targetSub}` : "another user";
  const mode = state.accessLevel === "read_write" ? "read/write" : "read-only";
  const message = state.expired ? `Impersonation session for ${who} has expired \u2014 re-authenticate to exit.` : `You are acting as ${who}, ${mode} (impersonated by ${state.actorSub}).`;
  return {
    message,
    actorSub: state.actorSub,
    targetSub: state.targetSub,
    sessionId: state.sessionId,
    reason: state.reason,
    expiresAt: state.expiresAt,
    expired: state.expired,
    accessLevel: state.accessLevel
  };
}

// src/auth/guest-session.ts
var GuestSignInError = class extends Error {
  constructor(message, code, status) {
    super(message);
    this.code = code;
    this.status = status;
    this.name = "GuestSignInError";
  }
  code;
  status;
  static {
    brandClass(this, "auth/GuestSignInError");
  }
};
var GUEST_SESSION_KEY = storageKey("auth", "guest-session");
var EXPIRY_MARGIN_MS = 6e4;
function storage() {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}
function readGuestSession(now = Date.now()) {
  try {
    const raw = storage()?.getItem(GUEST_SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (!session?.token || typeof session.expiresAt !== "number" || !session.guest?.id) return null;
    if (session.expiresAt - now <= EXPIRY_MARGIN_MS) {
      clearGuestSession();
      return null;
    }
    return session;
  } catch {
    return null;
  }
}
function saveGuestSession(session) {
  try {
    storage()?.setItem(GUEST_SESSION_KEY, JSON.stringify(session));
  } catch {
  }
}
function clearGuestSession() {
  try {
    storage()?.removeItem(GUEST_SESSION_KEY);
  } catch {
  }
}
async function post(base, route, body, fetchImpl) {
  const response = await fetchImpl(`${base.replace(/\/+$/, "")}/guest/${route}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "omit"
  });
  let payload = {};
  try {
    payload = await response.json();
  } catch {
  }
  if (!response.ok) {
    throw new GuestSignInError(
      String(payload.error_description ?? "Sign-in failed. Try again."),
      String(payload.error ?? "error"),
      response.status
    );
  }
  return payload;
}
async function requestGuestCode(authBase, input, fetchImpl = fetch) {
  const payload = await post(authBase, "challenge", { email: input.email, share_token: input.shareToken }, fetchImpl);
  return { challengeId: String(payload.challenge_id), expiresAt: Number(payload.expires_at) * 1e3 };
}
async function verifyGuestCode(authBase, input, fetchImpl = fetch) {
  const payload = await post(authBase, "verify", { challenge_id: input.challengeId, code: input.code.trim() }, fetchImpl);
  const guest = payload.guest;
  if (typeof payload.token !== "string" || !guest?.id) {
    throw new GuestSignInError("Sign-in failed. Try again.", "invalid_response", 502);
  }
  const session = { token: payload.token, expiresAt: Number(payload.expires_at) * 1e3, guest };
  saveGuestSession(session);
  return session;
}

// src/auth/auth0-env.ts
var AUTH0_TENANTS = {
  sandbox: {
    domain: "tenant22r23rfwefwfe.us.auth0.com",
    clientId: "nuDvszbwYz0z4qL1pmpIKPMpwqvwMowl"
  },
  staging: {
    domain: "tenant22r23rfwefwfe.us.auth0.com",
    clientId: "nuDvszbwYz0z4qL1pmpIKPMpwqvwMowl"
  },
  prd: {
    domain: "dev-8drlzxonkeqsd0jf.us.auth0.com",
    clientId: "h9F98oKMdCT5vHAwN1mf1izd6Z8Z4kf1"
  }
};
var PROD_CUSTOM_DOMAIN = "login.authe.io";
function auth0TenantEnv(env) {
  if (env === "prd") return "prd";
  if (env === "staging") return "staging";
  return "sandbox";
}
function resolveAuth0Config(options) {
  const tenant = AUTH0_TENANTS[auth0TenantEnv(options.env)];
  const overrides = options.overrides ?? {};
  const domain = overrides.domain ?? (options.env === "prd" && options.useCustomDomain ? PROD_CUSTOM_DOMAIN : tenant.domain);
  return {
    ...overrides,
    domain,
    clientId: overrides.clientId ?? tenant.clientId,
    audience: overrides.audience ?? `https://api.${options.rootDomain}`,
    // Env-specific claim namespace = `https://{rootDomain}/`, matching the
    // post-login Action's emitted namespace AND the backend authorizer's
    // DomainName (`https://sandbox.authe.io` in sandbox, `https://authe.io` in
    // prd). Reading claims under any other namespace yields `undefined`.
    claimsNamespace: overrides.claimsNamespace ?? `https://${options.rootDomain}/`
  };
}
function resolveAuth0ConfigFromEnv(env, options = {}) {
  return resolveAuth0Config({
    env: env.env,
    rootDomain: env.apiBaseDomain,
    useCustomDomain: options.useCustomDomain,
    // claimsNamespace is derived from rootDomain inside resolveAuth0Config
    // (`https://{rootDomain}/`) so it tracks the env-specific namespace the
    // post-login Action emits and the backend authorizer expects.
    overrides: {
      domain: env.auth0Domain,
      clientId: env.auth0ClientId,
      audience: env.auth0Audience,
      ...options.overrides
    }
  });
}

// src/auth/scope-catalog.ts
function splitScopeName(name) {
  const i = name.lastIndexOf(":");
  if (i <= 0) return { resource: name, action: "" };
  return { resource: name.slice(0, i), action: name.slice(i + 1) };
}
function resolveScopeCatalogUrl(source) {
  const base = source.baseUrl.replace(/\/+$/, "");
  const env = policyDataEnv(source.env);
  return `${base}/public/GET/cdn/${env}/global/permissions-data/role_permissions_with_scope_description.json`;
}
function parseScopeCatalog(json) {
  const rawRoles = Array.isArray(json) ? json : [];
  const byName = /* @__PURE__ */ new Map();
  const roles = [];
  for (const role of rawRoles) {
    if (!role?.name) continue;
    const scopeNames = [];
    for (const entry of role.scopes ?? []) {
      const name = typeof entry === "string" ? entry : entry?.name;
      if (!name) continue;
      const description = typeof entry === "string" ? "" : entry?.description ?? "";
      scopeNames.push(name);
      const existing = byName.get(name);
      if (!existing || !existing.description && description) {
        const { resource, action } = splitScopeName(name);
        byName.set(name, { name, resource, action, description });
      }
    }
    roles.push({ name: role.name, description: role.description, scopes: scopeNames });
  }
  const scopes = [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
  return { scopes, roles };
}
var catalogs = globalSingleton("auth/scope-catalogs", () => ({
  cache: /* @__PURE__ */ new Map(),
  inflight: singleflight()
}));
async function loadScopeCatalog(source, options = {}) {
  const url = typeof source === "string" ? source : resolveScopeCatalogUrl(source);
  if (options.force) {
    catalogs.cache.delete(url);
    catalogs.inflight.forget(url);
  }
  const cached = catalogs.cache.get(url);
  if (cached) return cached;
  return catalogs.inflight.run(url, async () => {
    const fetchImpl = options.fetchImpl ?? fetch;
    const res = await fetchImpl(url, { signal: options.signal });
    if (!res.ok) throw new Error(`scope catalog fetch failed: ${res.status} ${url}`);
    const catalog = parseScopeCatalog(JSON.parse(await res.text()));
    catalogs.cache.set(url, catalog);
    return catalog;
  });
}
function clearScopeCatalogCache() {
  catalogs.cache.clear();
}

// src/auth/build-time-policy.ts
var BUILD_TIME_POLICY_ENV_KEYS = ["CASBIN_POLICY", "CASBIN_PERMISSION_FROM_SSM"];
function resolveBuildTimePolicyCsv(source = {}, fallback = DEFAULT_RBAC_POLICY) {
  for (const key of BUILD_TIME_POLICY_ENV_KEYS) {
    const value = readEnvKey(source, key);
    if (value) return value;
  }
  return fallback;
}
function parseBuildTimePolicy(source = {}, fallback) {
  return parsePolicyCsv(resolveBuildTimePolicyCsv(source, fallback));
}
function permissionSetFromPolicyCsv(roleNames, csv) {
  return permissionSetFromPolicy(roleNames, parsePolicyCsv(csv));
}
function buildTimePermissionSet(options = {}) {
  return permissionSetFromPolicy(
    options.roles ?? [],
    parseBuildTimePolicy(options.source ?? {}, options.fallback)
  );
}

// src/auth/resource-groups.ts
var TEAM = "team";
var TEAM_MEMBERS = "team_members";
var TEAM_ROLES = "team_roles";
var TEAM_INVITES = "team_invites";
var TEAM_AUDIT = "team_audit";
var TEAM_MANAGEMENT_RESOURCES = [
  TEAM,
  TEAM_MEMBERS,
  TEAM_ROLES,
  TEAM_INVITES,
  TEAM_AUDIT
];
var COLLABORATIVE_RESOURCES = [
  "transactions",
  "addresses",
  "entities",
  "tags",
  "labels",
  "groups",
  "disputes",
  "streams",
  "pipelines",
  "workspaces",
  "notifications"
];
var RESOURCE_GROUP_LABEL = {
  transactions: "Transactions",
  addresses: "Addresses",
  entities: "Entities",
  tags: "Tags",
  labels: "Labels",
  groups: "Groups",
  disputes: "Disputes",
  streams: "Streams",
  pipelines: "Pipelines",
  workspaces: "Workspaces",
  notifications: "Notifications",
  [TEAM]: "Team settings",
  [TEAM_MEMBERS]: "Team members",
  [TEAM_ROLES]: "Roles",
  [TEAM_INVITES]: "Invites",
  [TEAM_AUDIT]: "Audit log"
};
var TEAM_GROUP_DESCRIPTION = {
  [TEAM]: "Rename the team and change its settings.",
  [TEAM_MEMBERS]: "Invite, edit, and remove members.",
  [TEAM_ROLES]: "Create and edit roles + permissions.",
  [TEAM_INVITES]: "Send, resend and revoke invitations.",
  [TEAM_AUDIT]: "View the team activity log."
};

// src/auth/permission-matrix.ts
var DEFAULT_ACTIONS = ["list", "show", "create", "edit", "delete", "export"];
var ACTION_LABEL = {
  list: "List",
  show: "View",
  create: "Create",
  edit: "Edit",
  delete: "Delete",
  export: "Export",
  [MANAGE_ACTION]: "Manage"
};
var DEFAULT_RESOURCE_GROUPS = [
  ...COLLABORATIVE_RESOURCES.map((id) => ({ id, label: RESOURCE_GROUP_LABEL[id] ?? id })),
  ...TEAM_MANAGEMENT_RESOURCES.map((id) => ({
    id,
    label: RESOURCE_GROUP_LABEL[id] ?? id,
    description: TEAM_GROUP_DESCRIPTION[id]
  }))
];
var actionsFor = (group) => group.actions ?? [...DEFAULT_ACTIONS];
function hasPermission(matrix, group, action) {
  const actions = matrix[group];
  if (!actions) return false;
  return actions.includes(action) || actions.includes(MANAGE_ACTION);
}
function togglePermission(matrix, group, action) {
  const current = new Set(matrix[group] ?? []);
  if (current.has(action)) current.delete(action);
  else current.add(action);
  const next = { ...matrix };
  if (current.size === 0) delete next[group];
  else next[group] = [...current];
  return next;
}
function setGroupActions(matrix, group, actions) {
  const next = { ...matrix };
  if (actions.length === 0) delete next[group];
  else next[group] = [...new Set(actions)];
  return next;
}
function countPermissions(matrix) {
  return Object.values(matrix).reduce((sum, actions) => sum + actions.length, 0);
}
function grantedGroupCount(matrix) {
  return Object.keys(matrix).length;
}
function groupFullyGranted(matrix, group) {
  const granted = matrix[group.id];
  if (!granted) return false;
  if (granted.includes(MANAGE_ACTION)) return true;
  return actionsFor(group).every((action) => granted.includes(action));
}
function matrixFromScopes(scopeNames) {
  const out = {};
  for (const scope of scopeNames) {
    const i = scope.lastIndexOf(":");
    if (i <= 0 || i === scope.length - 1) continue;
    const resource = scope.slice(0, i);
    const action = scope.slice(i + 1);
    const actions = out[resource] ??= [];
    if (!actions.includes(action)) actions.push(action);
  }
  return out;
}
function mergeMatrices(...matrices) {
  const out = {};
  for (const matrix of matrices) {
    for (const [group, actions] of Object.entries(matrix)) {
      const merged = /* @__PURE__ */ new Set([...out[group] ?? [], ...actions]);
      out[group] = merged.has(MANAGE_ACTION) ? [MANAGE_ACTION] : [...merged];
    }
  }
  return out;
}

// src/auth/abac.ts
function subjectUserId(subject) {
  return String(subject.userId ?? "");
}
function asSet(ids) {
  return new Set((ids ?? []).map((id) => String(id)));
}
function isOwner(subject, resource) {
  const uid = subjectUserId(subject);
  return uid !== "" && String(resource.ownerUserId ?? "") === uid;
}
function isExplicitlyShared(subject, resource) {
  const uid = subjectUserId(subject);
  return uid !== "" && asSet(resource.visibleToUserIds).has(uid);
}
function sharedTeamIds(subject, resource) {
  const resourceTeams = asSet(resource.visibleToTeamIds);
  return [...asSet(subject.teamIds)].filter((t) => resourceTeams.has(t));
}
function isTeamShared(subject, resource) {
  return sharedTeamIds(subject, resource).length > 0;
}
function canView(subject, resource, options = {}) {
  return options.rbacTenantAccess === true || isOwner(subject, resource) || isExplicitlyShared(subject, resource) || isTeamShared(subject, resource);
}
function canMutate(subject, resource, options = {}) {
  return canView(subject, resource, options);
}
function filterViewable(subject, resources, options = {}) {
  return [...resources].filter((r) => canView(subject, r, options));
}

export { ACTION_LABEL, APP_PREFETCH, AUTH0_TENANTS, BUILD_TIME_POLICY_ENV_KEYS, COLLABORATIVE_RESOURCES, DEFAULT_ACTIONS, DEFAULT_RESOURCE_GROUPS, GUEST_SESSION_KEY, GuestSignInError, PROD_CUSTOM_DOMAIN, RESOURCE_GROUP_LABEL, TEAM, TEAM_AUDIT, TEAM_GROUP_DESCRIPTION, TEAM_INVITES, TEAM_MANAGEMENT_RESOURCES, TEAM_MEMBERS, TEAM_ROLES, applyPrefetchPlan, auth0TenantEnv, buildPrefetchPlan, buildTimePermissionSet, canMutate, canView, clearGuestSession, clearScopeCatalogCache, countPermissions, createMockToken, entryAssets, filterViewable, getImpersonationState, grantedGroupCount, groupFullyGranted, hasPermission, impersonationBanner, isDevAuthEnabled, isExplicitlyShared, isImpersonating, isImpersonationExpired, isOwner, isTeamShared, loadScopeCatalog, matrixFromScopes, mergeMatrices, parseBuildTimePolicy, parseScopeCatalog, permissionSetFromPolicyCsv, platformPreconnect, readGuestSession, requestGuestCode, resolveAppPrefetch, resolveAuth0Config, resolveAuth0ConfigFromEnv, resolveBuildTimePolicyCsv, resolveScopeCatalogUrl, saveGuestSession, setGroupActions, sharedTeamIds, togglePermission, verifyGuestCode };
//# sourceMappingURL=chunk-UT7HCFJG.js.map
//# sourceMappingURL=chunk-UT7HCFJG.js.map